import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addCart, buyNow, deleteCartBatch, editCart, queryCart } from "../endpoints/cart";
import { useBackendLang } from "../lang-context";
import type { AddCartInput, EditCartInput } from "../types/cart";

const CART_KEY = ["cart", "list"] as const;

/** Stable mutation key for the add-to-cart mutation. Exported so a consumer
 *  that does NOT own the BuyRail's hook instance — notably the cart page after
 *  PDP "立即代购" navigates there — can observe a still-in-flight add via
 *  `useIsMutating({ mutationKey: ADD_CART_MUTATION_KEY })` and hold a skeleton
 *  instead of flashing the empty / pending-order state before the item lands
 *  (#431). */
export const ADD_CART_MUTATION_KEY = ["cart", "add"] as const;

/** Fetch the user's cart (requires JWT). When authToken is null the query is
 *  disabled so the page can show a sign-in CTA instead of pretending to fetch.
 *
 *  Threads the active backend `lang` (from `BackendLangProvider`) so the
 *  backend localizes item `subject` titles to match catalog/PDP. `lang` is part
 *  of the query key so a locale switch refetches localized titles instead of
 *  serving the stale-cached language (#211). */
export function useCart(authToken: string | null) {
  const lang = useBackendLang();
  return useQuery({
    queryKey: [...CART_KEY, lang],
    queryFn: () => queryCart({ baseUrl: API_BASE_URL }, authToken as string, {}, { lang }),
    enabled: !!authToken,
    staleTime: 30 * 1000,
  });
}

export function useAddCart(authToken: string | null) {
  const lang = useBackendLang();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ADD_CART_MUTATION_KEY,
    mutationFn: async (input: AddCartInput) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return addCart({ baseUrl: API_BASE_URL }, authToken, input, { lang });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CART_KEY }),
  });
}

export function useEditCart(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EditCartInput) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return editCart({ baseUrl: API_BASE_URL }, authToken, input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CART_KEY }),
  });
}

export function useDeleteCart(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return deleteCartBatch({ baseUrl: API_BASE_URL }, authToken, ids);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CART_KEY }),
  });
}

/** #626 立即购买 — 调 /shopcart/buyNowV2:一次请求建/复用购物车行 + 设置精确数量,
 *  返回 currentCartItemId。省去 addV2 + editV2 两次串行请求。
 *  成功后同样 invalidate 购物车缓存,使 badge 保持准确。*/
export function useBuyNow(authToken: string | null) {
  const lang = useBackendLang();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddCartInput) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return buyNow({ baseUrl: API_BASE_URL }, authToken, input, { lang });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CART_KEY }),
  });
}
